import { z } from "zod";
import { groupRoleSchema, groupMemberStatusSchema } from "./groups.js";

/**
 * Shared DTO cho Remove Member — DELETE /v1/groups/:id/members/:uid
 * (US-E4.F4-0027 / @UC0029 — Remove Member, Phase 3 RBAC `@gate`).
 *
 * Một nguồn sự thật cho api (shape response + mã lỗi) và mobile (gọi + map lỗi), tránh drift (DRY).
 *
 * ⚠️ FAIL-CLOSED tới Phase 7 (API_BUILD_PLAN:145 + 201, quyết định PO):
 *   Trong cửa sổ P3→P7, remove-member phải fail-closed — CHƯA tính được vị thế tài chính cá nhân
 *   (Ledger Phase 6 + debt_entries Phase 7 chưa build) ⇒ CHẶN hành động thật bằng feature-flag
 *   `GROUP_MEMBER_REMOVAL_ENABLED` (mặc định OFF). OFF ⇒ 409 REMOVAL_DISABLED, KHÔNG remove.
 *   KHÔNG "enforce non-financial rồi cho qua". Guards (self/owner/rbac/tenant) VẪN chạy đầy đủ.
 *
 * Khi flag ON (tương lai P7): remove = set GroupMember.status='removed' (SOFT-delete, preserve
 * history — KHÔNG hard-delete, KHÔNG xoá lịch sử giao dịch). AC2 debt-check khi ON = defer/stub
 * (TODO P7 — KHÔNG bịa số nợ). AC1 push notif = defer Phase 8.5.
 */

/**
 * Mã lỗi máy-đọc-được (RFC7807 problem+json `code`) cho remove-member — ổn định qua i18n/đổi wording.
 * Client discriminate theo `code`, KHÔNG so khớp message.
 */
export const REMOVE_MEMBER_CODE = {
  /** Flag OFF (fail-closed P3→P7): chưa hỗ trợ xoá thành viên tới khi có hệ thống công nợ. */
  REMOVAL_DISABLED: "GROUP_MEMBER_REMOVAL_DISABLED",
  /** AC3: không thể tự xoá chính mình. */
  CANNOT_REMOVE_SELF: "GROUP_MEMBER_CANNOT_REMOVE_SELF",
  /** AC4: không thể xoá chủ nhóm (Owner). */
  CANNOT_REMOVE_OWNER: "GROUP_MEMBER_CANNOT_REMOVE_OWNER",
} as const;

/**
 * Thông điệp lỗi (tiếng Việt sản phẩm) — một nguồn sự thật api ↔ mobile.
 * REMOVAL_DISABLED: wording fail-closed (chờ hệ thống công nợ Phase 7).
 */
export const REMOVAL_DISABLED_MESSAGE =
  "Chưa hỗ trợ xoá thành viên tới khi có hệ thống sổ cái (kiểm tra công nợ).";
export const CANNOT_REMOVE_SELF_MESSAGE = "Bạn không thể tự xoá chính mình khỏi nhóm.";
export const CANNOT_REMOVE_OWNER_MESSAGE = "Không thể xoá chủ nhóm.";

/**
 * Shape response cho DELETE /v1/groups/:id/members/:uid khi flag ON (remove thật thành công).
 *  - memberId: GroupMember.id đã chuyển status (soft-removed).
 *  - userId: User.id chủ membership đã bị xoá khỏi nhóm.
 *  - status: trạng thái membership SAU remove — US-E4.F2-0396: nay là **`join_pending`** (HÀNG ĐỢI),
 *    không còn `removed`. Soft-delete vẫn giữ row; khác biệt là có ĐƯỜNG QUAY LẠI (O/A/T add lại
 *    qua `POST /groups/:id/members/add`) thay vì một ngõ cụt.
 *    🔴 Cố ý trả trạng thái THẬT ở đây — khác `selfLeaveResponseSchema` (giữ literal `"left"` vì nó
 *    nói với chính người vừa rời, "đang chờ duyệt" sẽ sai nghĩa với họ). Response này nói với
 *    ADMIN: admin cần biết người đó nằm trong hàng đợi thì mới biết là add lại được.
 * `.strict()` chặn rò field thừa (vd lịch sử giao dịch/secret) ra response (C2).
 *
 * ⚠️ Trong cửa sổ P3→P7 (flag OFF) endpoint KHÔNG trả shape này — trả 409 REMOVAL_DISABLED.
 */
export const removeMemberResponseSchema = z
  .object({
    memberId: z.string().uuid(),
    userId: z.string().uuid(),
    status: groupMemberStatusSchema,
    role: groupRoleSchema,
  })
  .strict();

export type RemoveMemberResponse = z.infer<typeof removeMemberResponseSchema>;
